import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  DayExercises,
  groupExercisesByDay,
} from "@/shared/components/utils/RoutineMaper";
import { CheckBox } from "@/shared/components/atoms/CheckBox";
import { faPlayCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "@/shared/components/organisms/Modal";
import i18n from "@/app/i18n";
import {
  useDownloadPdfMutation,
  useGetRoutinesByIdQuery,
  useGetUserPlaningForPositionQuery,
} from "../api/user-active-plan.api";
import { BASE_URL } from "@/app/app.api";

const previewVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const PreviewRoutine = ({ id, user_id, position }) => {
  const { data, isLoading } = useGetRoutinesByIdQuery({ id });
  const { data: positionData, isLoading: positionLoading } =
    useGetUserPlaningForPositionQuery({ position });

  const loading = position ? positionLoading : isLoading;
  const renderData = position ? positionData : data;

  const pdf_uri = `${BASE_URL}/view-pdf-all/${user_id}/${renderData?.pCustomize?.position}/p_customizes/${i18n.language?.split("-")[0]}`;

  const [routines, setRoutines] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [routine, setRoutine] = useState({
    id: 27,
    position: 27,
    plan: "N36",
    objective: "Hipertrofia",
    age: "16-20",
    day_count: 5,
    day_week: "L-M-X-V-S",
    week: 4,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [item, setSelectItem] = useState({
    url: "",
    name: "",
    review: "",
    second_main_muscle: "",
    second_secondary_muscle: "",
  });

  useEffect(() => {
    if (renderData) {
      const result = groupExercisesByDay(renderData?.routines);
      setRoutines(result);
      setRoutine(renderData?.pCustomize);
    }
  }, [id, data, position, positionData]);

  const TabsDays =
    routines?.map((day, index) => ({
      id: index + 1,
      name: day.day,
      index,
      label: routine?.day_week?.split("-")[index],
    })) || [];

  const handleImageClick = ({
    url,
    name,
    review,
    second_main_muscle,
    second_secondary_muscle,
  }) => {
    setSelectItem({
      url,
      name,
      review,
      second_main_muscle,
      second_secondary_muscle,
    });
    setIsModalOpen(true);
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={previewVariants}
      transition={{ duration: 0.5 }}
      className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
    >
      {loading ? (
        <div className="animate-pulse flex flex-col space-y-4">
          {[...Array(10)].map((_, index) => (
            <div key={index} className="h-6 bg-gray-300 rounded"></div>
          ))}
        </div>
      ) : (
        <>
          <div className="sticky top-0 bg-white z-10 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-4">
            <div className="flex flex-wrap gap-2 sm:gap-4">
              {TabsDays.map((day) => (
                <motion.button
                  key={day.id}
                  onClick={() => handleTabChange(day.index)}
                  className={`py-2 px-3 sm:px-4 rounded text-sm sm:text-base ${
                    activeTab === day.index
                      ? "bg-primary text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  whileTap={{ scale: 0.95 }}
                  initial={{ y: 0 }}
                  animate={activeTab === day.index ? { y: -5 } : { y: 0 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {day.label}
                </motion.button>
              ))}
            </div>
            <a
              target="_blank"
              href={pdf_uri}
              className=" hover:underline text-sm sm:text-base"
            >
              Descargar PDF
            </a>
          </div>

          {routines?.[activeTab] && (
            <div className="space-y-8">
              {routines[activeTab]?.exercises?.map((exercise, index) => (
                <div key={index} className="p-6 rounded-lg bg-gray-50">
                  <h2 className="text-xl font-semibold bg-gray-200 py-3 px-4 rounded mb-6">
                    {exercise.category}
                  </h2>
                  <div className="space-y-6">
                    {exercise?.exercises.map((ex, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col lg:flex-row items-center justify-between border-b py-4 gap-6"
                      >
                        <div className="relative w-24 h-24 flex-shrink-0">
                          <img
                            src={ex.exercise.image_url}
                            className="w-full h-full rounded-full object-cover"
                            alt={ex.exercise.name}
                          />
                          <div
                            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-full cursor-pointer"
                            onClick={() =>
                              handleImageClick({
                                url: ex.exercise.video_url,
                                name: ex.exercise?.exercise,
                                review: ex.exercise?.review,
                                second_main_muscle:
                                  ex.exercise?.second_main_muscle,
                                second_secondary_muscle:
                                  ex.exercise?.second_secondary_muscle,
                              })
                            }
                          >
                            <FontAwesomeIcon
                              icon={faPlayCircle}
                              className="text-white text-3xl"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-8 text-center flex-1">
                          <div>
                            <label className="text-sm font-medium">
                              Series
                            </label>
                            <span className="block text-gray-600">
                              {ex?.serie}
                            </span>
                          </div>
                          <div>
                            <label className="text-sm font-medium">
                              Reps/Tiempo
                            </label>
                            <span className="block text-gray-600">
                              {ex?.repetitions}{" "}
                              {ex?.exercise?.um?.toLowerCase()}
                            </span>
                          </div>
                          <div>
                            <label className="text-sm font-medium">
                              Intensidad
                            </label>
                            <span className="block text-gray-600">
                              {ex?.intensity} %
                            </span>
                          </div>
                          <div>
                            <label className="text-sm font-medium">
                              Descanso
                            </label>
                            <span className="block text-gray-600">
                              {ex?.break} seg
                            </span>
                          </div>
                          <div>
                            <label className="text-sm font-medium">
                              Super Serie
                            </label>
                            <div className="mt-1">
                              <CheckBox value={ex?.super_serie !== "0"} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!routines?.[activeTab] && (
              <>
                <div class="w-full rounded-md bg-yellow-100 p-4 text-yellow-800 border border-yellow-300">
                  No hay un plan de entrenamiento disponible actualmente.
                </div>
              </>
          )}

          <Modal
            open={isModalOpen}
            Close={() => setIsModalOpen(false)}
            tittleModal={item?.name}
          >
            <div className="flex flex-col gap-6 max-w-3xl mx-auto">
              {item?.url && (
                <video
                  loop
                  src={item?.url}
                  muted
                  autoPlay
                  className="w-full max-h-[70vh] object-contain rounded-lg"
                />
              )}
              {item?.review &&
                item?.review !== "" &&
                item?.review !== "NULL" && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-600 text-base">
                      {item?.review
                        .split("/b")
                        .filter((tip) => tip.trim())
                        .map((tip, index) => (
                          <span key={index} className="block mt-4">
                            {"  "}• {tip.trim()}
                          </span>
                        ))}
                    </p>
                  </div>
                )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {item?.second_main_muscle &&
                  item?.second_main_muscle !== "" && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <label className="text-sm font-medium">
                        Muscular Principal
                      </label>
                      <span className="block text-gray-600">
                        {item?.second_main_muscle}
                      </span>
                    </div>
                  )}
                {item?.second_secondary_muscle &&
                  item?.second_secondary_muscle !== "" && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <label className="text-sm font-medium">
                        Muscular Secundario
                      </label>
                      <span className="block text-gray-600">
                        {item?.second_secondary_muscle}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          </Modal>
        </>
      )}
    </motion.div>
  );
};

export default PreviewRoutine;
